import { Card, CardContent, IPColor, IPStatus } from '@components/LPCard/Common'
import { LP_STATUS } from '@utils/constants/learningPath'
import styled, { css } from 'styled-components'
import { IIsActive, ITransitionAnimation } from './interface'

const generateIconStyles = (bg: string, color: string, fontSize: string) => css`
  background-color: ${({ theme }) => theme.palette[bg]};
  svg {
    font-size: ${fontSize};
    color: ${({ theme }) => theme.palette[color]};
  }
`

const generatePulseStyles = (bg: string) => css`
  @keyframes pulse-blue {
    0% {
      transform: scale(0.98);
      box-shadow: 0 0 0 0 rgba(30, 144, 255, 0.5);
    }

    70% {
      transform: scale(1);
      box-shadow: 0 0 0 24px rgba(30, 144, 255, 0);
    }

    100% {
      transform: scale(0.98);
      box-shadow: 0 0 0 0 rgba(30, 144, 255, 0);
    }
  }

  background-color: ${({ theme }) => theme.palette[bg]};
  box-shadow: 0 0 0 0 rgba(30, 144, 255, 1);
  animation: pulse-blue 2s infinite;
`

const generateWidth = (val: string) => css`
  width: ${val} !important;
`

const generateOpacity = (val: string) => css`
  opacity: ${val} !important;
`

const generateLeft = (val: string) => css`
  left: ${val} !important;
`

const generateDisplay = (val: string) => css`
  display: ${val} !important;
`

export const SessionPanelWrapper = styled.div`
  display: flex;
  position: absolute;
  right: 0;
  z-index: 999;
  bottom: 50px;
`

export const SlideIn = styled.div<ITransitionAnimation>`
  padding: 0 !important;

  ${({ duration = 100 }) => css`
    transition: ${duration}ms;
  `}

  ${({ state }) => state === 'entering' && generateWidth('0')}
  ${({ state }) => state === 'entered' && generateWidth('272px')}
  ${({ state }) => state === 'exiting' && generateWidth('272px')}
  ${({ state }) => state === 'exited' && generateWidth('0')}
`

export const ButtonText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xxxs};
  padding-top: ${({ theme }) => theme.space.xs};
  position: relative;
  text-align: center;
  color: ${({ theme }) => theme.colors.text02};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  width: ${({ theme }) => theme.layout.xl};
`

export const FadeIn = styled.div<ITransitionAnimation & IPColor>`
  width: 272px;
  height: 384px;

  ${Card} {
    width: 100%;
    height: 100%;
  }

  ${CardContent} {
    width: 100%;
    height: 100%;
  }

  ${({ theme, textColor = 'charcoal' }) => css`
    color: ${theme.palette[textColor]};
  `}

  ${({ duration = 300 }) => css`
    transition: ${duration}ms;
  `}

  > div:nth-child(2) {
    border-radius: 12px 0 0 12px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    > div:nth-child(2) {
      height: 364px;
    }
  }

  ${({ state }) => state === 'entering' && generateDisplay('block')}
  ${({ state }) => state === 'exited' && generateDisplay('none')}
  ${({ state }) => state === 'entering' && generateOpacity('0')}
  ${({ state }) => state === 'entered' && generateOpacity('1')}
  ${({ state }) => state === 'exiting' && generateWidth('1')}
  ${({ state }) => state === 'exited' && generateOpacity('0')}
`

export const ToggleDrawer = styled.div`
  display: flex;
`

export const ButtonContainer = styled.div<ITransitionAnimation & IIsActive>`
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'no-drop')};
  position: absolute;
  bottom: 0;
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ state }) => state === 'entering' && generateLeft('-80px')}
  ${({ state }) => state === 'entered' && generateLeft('0px')}
  ${({ state }) => state === 'exiting' && generateLeft('0px')}
  ${({ state }) => state === 'exited' && generateLeft('-100px')}
  ${({ state }) =>
    state !== 'exited' &&
    css`
      ${ButtonText} {
        display: none;
      }
    `};
`

export const CircleButton = styled.div<IPStatus & ITransitionAnimation & IIsActive>`
  border-radius: 50%;
  height: 68px;
  width: 68px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.37);
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'no-drop')};
  position: absolute;
  bottom: ${({ theme }) => theme.space.xl};

  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ status }) => status === LP_STATUS.LOCKED && generateIconStyles('gray20', 'gray100', '34px')};
  ${({ status }) =>
    (status === LP_STATUS.UNLOCKED || status === LP_STATUS.CUSTOM_BOOKED) &&
    generateIconStyles('info100', 'white', '34px')};
  ${({ status, state }) =>
    state === 'exited' &&
    (status === LP_STATUS.UNLOCKED || status === LP_STATUS.CUSTOM_BOOKED) &&
    generatePulseStyles('info100')};
  ${({ status }) => status === LP_STATUS.COMPLETE && generateIconStyles('green100', 'charcoal', '34px')};
`

export const CircleBG = styled.div<IPStatus>`
  height: 26px;
  width: 26px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.37);
  border-radius: 50%;
  position: absolute;
  right: -10px;
  bottom: 5px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ status }) => status === LP_STATUS.LOCKED && generateIconStyles('white', 'danger100', '14px')}
  ${({ status }) =>
    [LP_STATUS.UNLOCKED, LP_STATUS.COMPLETE, LP_STATUS.CUSTOM_BOOKED].includes(status) &&
    generateIconStyles('white', 'green100', '14px')}
`

export const CloseContainer = styled.div<IPStatus>`
  top: ${({ theme }) => theme.space.lg};
  right: ${({ theme }) => theme.space.xs};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  position: relative;
  cursor: pointer;
  svg {
    z-index: 1;
  }

  ${({ theme, status, Unit }) =>
    ([LP_STATUS.LOCKED, LP_STATUS.UNLOCKED, LP_STATUS.CUSTOM_BOOKED].includes(status) ||
      ([LP_STATUS.COMPLETE].includes(status) && !Unit?.Session?.Materials?.length)) &&
    generateIconStyles('inherit', 'inherit', theme.fontSize.xs)}
  ${({ theme, status, Unit }) =>
    [LP_STATUS.COMPLETE].includes(status) &&
    (Unit?.Session?.Materials?.length || !Unit) &&
    generateIconStyles('inherit', 'white', theme.fontSize.xs)}
`
